import {
    ContentButton,
    formatAddress,
    PortalUrls,
    ShortDateText,
    usePortalMode,
} from '@servicetitan/cp-ui';
import { BodyText, Button, Card, Headline, Icon, Stack } from '@servicetitan/design-system';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC } from 'react';
import { EstimateContainerWebModel } from '../../../api/portal-dashboard.api';
import { ProposalsListItemStatus } from './proposals-list-item-status';
import * as Styles from './proposals-list-item.module.less';

interface ProposalsListItemProps {
    className?: string;
    proposal: EstimateContainerWebModel;
    onViewEstimatesClick: (proposalId: string) => void;
}

export const ProposalsListItem: FC<ProposalsListItemProps> = observer(props => {
    const { className, onViewEstimatesClick, proposal } = props;

    const mode = usePortalMode();

    const handleViewClick = () => {
        onViewEstimatesClick(props.proposal.id);
    };

    return (
        <Card padding="thin" className={className}>
            <Card.Section>
                <Stack
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={mode.mobile ? 'baseline' : 'center'}
                    direction={mode.mobile ? 'column' : 'row'}
                >
                    <Stack alignItems="center" spacing={1} className="min-w-0 max-w-100">
                        <Headline size="medium" className="m-b-0 t-truncate">
                            {proposal.name}
                        </Headline>
                        <Stack.Item shrink={0}>
                            <ProposalsListItemStatus proposal={proposal} />
                        </Stack.Item>
                    </Stack>
                    <Stack.Item shrink={0}>
                        <ContentButton
                            primary
                            size="small"
                            href={`${PortalUrls.proposals.root}/${proposal.id}`}
                        >
                            View & Sign Proposal
                        </ContentButton>
                    </Stack.Item>
                </Stack>
            </Card.Section>
            <Card.Section>
                <Stack
                    spacing={3}
                    alignItems="baseline"
                    wrap={mode.desktop ? 'nowrap' : 'wrap'}
                    className="m-b--1"
                >
                    <Stack.Item
                        className={classNames(
                            Styles.whiteSpaceNoWrap,
                            Styles.minWidthContent,
                            'm-b-1',
                            {
                                'w-100': mode.mobile,
                            }
                        )}
                    >
                        <BodyText el="span" subdued size="small">
                            <Icon name="local_offer" size={16} className="m-r-half" />
                            {proposal.estimates.length} Estimate
                            {proposal.estimates.length !== 1 ? 's' : ''}
                        </BodyText>
                        {proposal.estimates.length > 0 && (
                            <Button
                                size="xsmall"
                                outline
                                className="m-l-1"
                                onClick={handleViewClick}
                            >
                                View
                            </Button>
                        )}
                    </Stack.Item>
                    <BodyText
                        el="span"
                        subdued
                        size="small"
                        className={classNames(
                            Styles.whiteSpaceNoWrap,
                            Styles.minWidthContent,
                            'm-b-1',
                            {
                                'w-100': mode.mobile,
                            }
                        )}
                    >
                        <Icon name="event" size={16} className="m-r-half" />
                        Created on{' '}
                        <ShortDateText date={proposal.createdOn} size="small" subdued inline />
                    </BodyText>
                    {!!proposal.serviceLocation?.address && (
                        <Stack.Item className="m-b-1">
                            <BodyText subdued size="small" className="t-truncate">
                                <Icon name="place" size={16} className="m-r-half m-b-half" />
                                {formatAddress(proposal.serviceLocation.address)}
                            </BodyText>
                        </Stack.Item>
                    )}
                </Stack>
            </Card.Section>
        </Card>
    );
});
